import { Button } from "@procurement-agents/ui";
import { ApiError, type AttachmentMeta } from "@procurement-agents/scope-api";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useScopeApi } from "../api";
import { soundAllowed } from "@procurement-agents/voice";
import { useHandsFree, useHandsFreeHold, VoiceControls } from "../handsFree";
import { AttachmentPicker } from "../renderers/Attachments";
import { Heard, MicButton, MicMessage, insertAt, useIntakeVoice, useMicMessage, warmSpeech } from "../voice";

/** The first question, asked before any request exists. Sending creates the run with the answer, so abandoned
 * clicks on "New request" leave nothing behind and the list never shows an untitled row. */
export function NewRequest({ opening }: { opening?: string | null } = {}) {
  const { api, toast } = useScopeApi();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const hf = useHandsFree();
  // /scope/new?hands_free=1, the home-screen "Voice request" shortcut: switch Voice on on arrival
  const autoStart = params.get("hands_free") === "1" && hf.available;
  const [tapToBegin, setTapToBegin] = useState(false);
  const startButton = useRef<HTMLButtonElement>(null);
  useHandsFreeHold(hf.active);   // leaving this page for anywhere but the request it creates ends the session
  const [prompt, setPrompt] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState<AttachmentMeta[]>([]);
  const [sending, setSending] = useState(false);
  const [heard, setHeard] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  // Dictation before the request exists: speech here has no run yet and is adopted by the one this page creates.
  // The page has two voice controls, the mic on the composer and Start by voice; the Voice toggle is the intake's.
  const voice = useIntakeVoice();
  const [micMessage, setMicMessage, dismissMicMessage] = useMicMessage();
  const dictated = (words: string) => { setHeard((h) => (h ? `${h} ${words}` : words)); setText((t) => insertAt(t, words, box.current)); };
  useEffect(() => { api.tenant().then((t) => setPrompt(t.first_question?.prompt ?? "What are you planning to procure?")).catch(() => setPrompt("What are you planning to procure?")); }, [api]);
  useEffect(() => { if (!autoStart) box.current?.focus(); }, [prompt, autoStart]);
  // Sound needs a tap first on iPhone, always, and on most browsers unless the app is installed: try, and where it
  // is not allowed leave Voice on with "Tap to begin", focused. A plain page load with Voice remembered on never
  // opens the microphone by itself: it offers "Tap to resume".
  useEffect(() => {
    if (!autoStart || hf.active) return;
    warmSpeech(api);   // by the time anyone taps "Tap to begin", the connection is open
    hf.prefer(true);
    let live = true;
    void soundAllowed().then((ok) => {
      if (!live) return;
      if (ok) void hf.begin();
      else { setTapToBegin(true); requestAnimationFrame(() => startButton.current?.focus()); }
    });
    return () => { live = false; };
  }, [autoStart]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Hands-free (CHANGE_voice_level3): the opening and the first question are said, and the answer creates the request
  // started by voice. Skip and go back have nothing to act on here: the question is required and it is the first.
  useEffect(() => {
    if (!hf.active || !prompt) return;
    hf.attach(null, async (reply) => {
      if (typeof reply !== "object" || !("text" in reply)) return;
      const r = await api.createRun({ follow_ups: true, hands_free: true }, reply);   // Voice on: the run starts with it on
      nav(`../runs/${r.run_id}`, { relative: "path" });
    });
    hf.setTurn({ key: "first-question", kind: "question", text: [opening, prompt].filter(Boolean).join(" "), prompt, required: true, canGoBack: false });
  }, [hf.active, prompt]);   // eslint-disable-line react-hooks/exhaustive-deps
  // documents are uploaded before the request exists; creating it adopts them, so the first answer and its
  // documents reach the same extraction
  const upload = useCallback((file: File) => api.uploadPendingAttachment(file), [api]);
  const ready = !!text.trim() || files.length > 0;
  const send = async () => {
    if (!ready || sending) return;
    setSending(true);
    voice.interrupt();
    const via = heard ? { via: "voice" as const } : {};
    const reply = files.length ? { text: text.trim(), attachment_ids: files.map((f) => f.attachment_id), ...via } : heard ? { text: text.trim(), ...via } : text.trim();
    try { const r = await api.createRun({ follow_ups: true, ...(hf.available && hf.on ? { hands_free: true } : {}) }, reply); nav(`../runs/${r.run_id}`, { relative: "path" }); }
    catch (e) { toast(e instanceof ApiError ? e.detail : String(e), "warn"); setSending(false); }
  };
  const hasContent = ready;   // switching Voice on would have to throw away what is in the box, so it waits for it to go
  return (
    <div className={`page narrow new-request${hf.active ? " voice-on" : ""}`}>
      <Link to=".." relative="path" className="back-link">← All requests</Link>
      <h2 className="run-title">New request</h2>
      <VoiceControls disabled={hasContent || sending} disabledHint="Clear the box, or send it, to use Voice." tapLabel={tapToBegin ? "Tap to begin" : "Tap to resume"} tapRef={startButton}
        description="The agent asks, you answer. Sends when you stop talking." />
      <div className="chat">
        {prompt ? <div className="turn agent">{prompt}</div> : <p className="muted small">Loading…</p>}
      </div>
      <form className="composer" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <AttachmentPicker upload={upload} items={files} onChange={setFiles} disabled={sending}
          actions={<><MicButton voice={voice} onText={dictated} disabled={sending} onMessage={setMicMessage} /><Button variant="primary" type="submit" disabled={!ready || sending}>{sending ? "Starting…" : "Send"}</Button></>}>
          <textarea ref={box} rows={5} value={text} onChange={(e) => { voice.interrupt(); setText(e.target.value); if (!e.target.value.trim()) setHeard(null); }} aria-label="Your answer" placeholder="Describe what you are planning to procure, or attach a document. Enter to send, Shift+Enter for a new line." disabled={sending}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void send(); } }} />
        </AttachmentPicker>
        <Heard text={heard} />
        <MicMessage message={micMessage} onDismiss={dismissMicMessage} />
      </form>
    </div>
  );
}
